import { useEffect, useRef, useCallback } from 'react';
import { useQueryClient, useQuery, useMutation } from '@tanstack/react-query';
import { notificationsApi, INotification, INotificationsQuery } from '../api';

const QUERY_KEY = ['notifications'] as const;

export function useNotifications(params: INotificationsQuery = { page: 1, limit: 10 }) {
  const queryClient = useQueryClient();
  const sseRef = useRef<EventSource | null>(null);

  const query = useQuery({
    queryKey: [...QUERY_KEY, params],
    queryFn: () => notificationsApi.list(params),
    refetchInterval: () => {
      return sseRef.current?.readyState === EventSource.OPEN ? false : 30_000;
    },
  });

  const addToCache = useCallback((notification: INotification) => {
    queryClient.setQueryData<ReturnType<typeof notificationsApi.list> extends Promise<infer T> ? T : never>(
      [...QUERY_KEY, params],
      (old) => {
        if (!old) return old;
        return {
          ...old,
          data: [notification, ...old.data].slice(0, params.limit ?? 10),
          total: old.total + 1,
          unreadCount: old.unreadCount + 1,
        };
      },
    );
    queryClient.invalidateQueries({ queryKey: QUERY_KEY });
  }, [queryClient, params]);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    if (!token) return;

    const connect = () => {
      const es = new EventSource(`/api/notifications/stream?token=${token}`);
      sseRef.current = es;

      es.onmessage = (event) => {
        try {
          const notification: INotification = JSON.parse(event.data);
          addToCache(notification);
          window.dispatchEvent(new CustomEvent('notification:new', { detail: notification }));
        } catch {
          // malformed payload
        }
      };

      es.onerror = () => {
        es.close();
        sseRef.current = null;
      };
    };

    connect();

    return () => {
      sseRef.current?.close();
      sseRef.current = null;
    };
  }, [addToCache]);

  const markRead = useMutation({
    mutationFn: notificationsApi.markRead,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });

  const markAllRead = useMutation({
    mutationFn: notificationsApi.markAllRead,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });

  const remove = useMutation({
    mutationFn: notificationsApi.remove,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QUERY_KEY }),
  });

  return {
    notifications: query.data?.data ?? [],
    total: query.data?.total ?? 0,
    unreadCount: query.data?.unreadCount ?? 0,
    isLoading: query.isLoading,
    markRead: markRead.mutate,
    markAllRead: markAllRead.mutate,
    remove: remove.mutate,
    refetch: query.refetch,
  };
}
